import { Checkbox,Divider,FormControlLabel,FormGroup,Stack,Typography } from "@mui/material";
import { useCallback,useContext,useEffect } from "react";
import { useTranslation } from "react-i18next";
import { NewFormContext } from "../../context/NewFormContext";
import { ChecklistSubitem,ChecklistType } from "../../types/checklist/types";

const key = "checklists.subitemModal.";

type props = {
  subitem: ChecklistSubitem;
  indexSubitem: number;
  itemId: string;
};

export default function SubitemPermissions({ subitem, indexSubitem, itemId }: props) {
  const { t } = useTranslation("translation");
  const { setModelo, modelo } = useContext(NewFormContext);

  const setCheckedPermission = useCallback(
    (key: string, value: boolean) => {
      setModelo((prev: ChecklistType) => ({
        ...prev,
        itens: prev.itens.map((item) =>
          item.id === itemId
            ? {
                ...item,
                subitens: item.subitens.map((subitem) => (subitem.id === indexSubitem ? { ...subitem, [key]: value } : subitem)),
              }
            : item,
        ),
      }));
    },
    [indexSubitem, itemId, setModelo],
  );

  const setCheckedMandatory = useCallback(
    (key: string, value: boolean) => {
      setModelo((prev: ChecklistType) => ({
        ...prev,
        itens: prev.itens.map((item) =>
          item.id === itemId
            ? {
                ...item,
                subitens: item.subitens.map((subitem) =>
                  subitem.id === indexSubitem ? { ...subitem, obrigatoriedade: { ...subitem.obrigatoriedade, [key]: value } } : subitem,
                ),
              }
            : item,
        ),
      }));
    },
    [indexSubitem, itemId, setModelo],
  );

  const setCheckedMandatoryAvaliation = useCallback(
    (subKey: "conforme" | "naoConforme" | "NA", key: string, value: boolean) => {
      setModelo((prev: ChecklistType) => ({
        ...prev,
        itens: prev.itens.map((item) =>
          item.id === itemId
            ? {
                ...item,
                subitens: item.subitens.map((subitem) =>
                  subitem.id === indexSubitem
                    ? {
                        ...subitem,
                        obrigatoriedade: {
                          ...subitem.obrigatoriedade,
                          [subKey]: {
                            ...subitem?.obrigatoriedade?.[subKey],
                            [key]: value,
                          },
                        },
                      }
                    : subitem,
                ),
              }
            : item,
        ),
      }));
    },
    [indexSubitem, itemId, setModelo],
  );

  useEffect(() => {
    if (!subitem.foto && subitem.tipo !== "avaliacao") {
      setCheckedPermission("galeriaFoto", false);
      setCheckedMandatory("img", false);
    }
    if (!subitem.comentario) {
      setCheckedMandatory("ocorrencia", false);
    }
    if (!subitem.permiteArquivo) {
      setCheckedMandatory("obrigaArquivo", false);
      setCheckedMandatoryAvaliation("conforme", "obrigaArquivo", false);
      setCheckedMandatoryAvaliation("naoConforme", "obrigaArquivo", false);
      setCheckedMandatoryAvaliation("NA", "obrigaArquivo", false);
    }
    if (subitem.hideImg === true) {
      setCheckedPermission("galeriaFoto", false);
      setCheckedMandatoryAvaliation("conforme", "img", false);
      setCheckedMandatoryAvaliation("naoConforme", "img", false);
      setCheckedMandatoryAvaliation("NA", "img", false);
    }
    if (subitem.hideComent === true) {
      setCheckedMandatoryAvaliation("conforme", "ocorrencia", false);
      setCheckedMandatoryAvaliation("naoConforme", "ocorrencia", false);
      setCheckedMandatoryAvaliation("NA", "ocorrencia", false);
    }
  }, [
    subitem.tipo,
    subitem.hideImg,
    subitem.hideComent,
    subitem.foto,
    subitem.comentario,
    subitem.permiteArquivo,
    setCheckedPermission,
    setCheckedMandatory,
    setCheckedMandatoryAvaliation,
  ]);

  function switchPermission(tipo: string) {
    switch (tipo) {
      case "avaliacao":
        return (
          <FormGroup row>
            <FormControlLabel
              labelPlacement="start"
              label="Abertura de plano de ação"
              control={<Checkbox checked={subitem.abrePlano ?? false} onChange={(eve) => setCheckedPermission("abrePlano", eve.target.checked)} />}
            />
            <Divider orientation="vertical" flexItem sx={{ marginLeft: 1 }} />
            <FormControlLabel
              labelPlacement="start"
              label="Foto"
              control={<Checkbox checked={!subitem?.hideImg as boolean} onChange={(eve) => setCheckedPermission("hideImg", !eve.target.checked)} />}
            />
            <Divider orientation="vertical" flexItem sx={{ marginLeft: 1 }} />
            <FormControlLabel
              labelPlacement="start"
              label="Comentário"
              control={
                <Checkbox checked={!subitem?.hideComent as boolean} onChange={(eve) => setCheckedPermission("hideComent", !eve.target.checked)} />
              }
            />
            {!modelo?.preenchimentoDeslogado && (
              <>
                <Divider orientation="vertical" flexItem sx={{ marginLeft: 1 }} />
                <FormControlLabel
                  labelPlacement="start"
                  label="Arquivo"
                  control={
                    <Checkbox
                      checked={subitem.permiteArquivo ?? false}
                      onChange={(eve) => setCheckedPermission("permiteArquivo", eve.target.checked)}
                    />
                  }
                />
              </>
            )}
            <Divider orientation="vertical" flexItem sx={{ marginLeft: 1 }} />
            <FormControlLabel
              disabled={subitem.hideImg === true}
              labelPlacement="start"
              label="Galeria"
              control={
                <Checkbox
                  checked={(subitem?.galeriaFoto as boolean) ?? false}
                  onChange={(eve) => setCheckedPermission("galeriaFoto", eve.target.checked)}
                />
              }
            />
          </FormGroup>
        );
      case "multiple":
      case "unique":
      case "numero":
      case "assinatura":
      case "valorMedida":
      case "list":
        return (
          <FormGroup row>
            <FormControlLabel
              labelPlacement="start"
              label="Foto"
              control={
                <Checkbox checked={(subitem?.foto as boolean) ?? false} onChange={(eve) => setCheckedPermission("foto", eve.target.checked)} />
              }
            />
            <Divider orientation="vertical" flexItem sx={{ marginLeft: 1 }} />
            <FormControlLabel
              labelPlacement="start"
              label="Comentário"
              control={
                <Checkbox
                  checked={(subitem?.comentario as boolean) ?? false}
                  onChange={(eve) => setCheckedPermission("comentario", eve.target.checked)}
                />
              }
            />
            {!modelo?.preenchimentoDeslogado && (
              <>
                <Divider orientation="vertical" flexItem sx={{ marginLeft: 1 }} />
                <FormControlLabel
                  labelPlacement="start"
                  label="Arquivo"
                  control={
                    <Checkbox
                      checked={subitem.permiteArquivo ?? false}
                      onChange={(eve) => setCheckedPermission("permiteArquivo", eve.target.checked)}
                    />
                  }
                />
              </>
            )}
            <Divider orientation="vertical" flexItem sx={{ marginLeft: 1 }} />
            <FormControlLabel
              disabled={!subitem.foto}
              labelPlacement="start"
              label="Galeria"
              control={
                <Checkbox checked={subitem?.galeriaFoto as boolean} onChange={(eve) => setCheckedPermission("galeriaFoto", eve.target.checked)} />
              }
            />
          </FormGroup>
        );
      case "data":
      case "hora":
        return (
          <FormGroup row>
            <FormControlLabel
              labelPlacement="start"
              label="Comentário"
              control={
                <Checkbox
                  checked={(subitem?.comentario as boolean) ?? false}
                  onChange={(eve) => setCheckedPermission("comentario", eve.target.checked)}
                />
              }
            />
          </FormGroup>
        );
      case "email":
      case "telefone":
      case "rg":
      case "cpfcnpj":
      case "cep":
      case "placa":
      case "textoCurto":
      case "textoLongo":
        return (
          <FormGroup row>
            <FormControlLabel
              labelPlacement="start"
              label="Foto"
              control={<Checkbox checked={(subitem.foto as boolean) ?? false} onChange={(eve) => setCheckedPermission("foto", eve.target.checked)} />}
            />
            {!modelo?.preenchimentoDeslogado && (
              <>
                <Divider orientation="vertical" flexItem sx={{ marginLeft: 1 }} />
                <FormControlLabel
                  labelPlacement="start"
                  label="Arquivo"
                  control={
                    <Checkbox
                      checked={subitem?.permiteArquivo ?? false}
                      onChange={(eve) => setCheckedPermission("permiteArquivo", eve.target.checked)}
                    />
                  }
                />
              </>
            )}
            <Divider orientation="vertical" flexItem sx={{ marginLeft: 1 }} />
            <FormControlLabel
              disabled={!subitem.foto}
              labelPlacement="start"
              label="Galeria"
              control={
                <Checkbox
                  checked={(subitem.galeriaFoto as boolean) ?? false}
                  onChange={(eve) => setCheckedPermission("galeriaFoto", eve.target.checked)}
                />
              }
            />
          </FormGroup>
        );
    }
  }

  function switchMandatory(tipo: string) {
    switch (tipo) {
      case "avaliacao":
        return (
          <Stack direction="column" spacing={1} justifyContent="flex-start" alignItems="flex-start">
            <FormControlLabel
              labelPlacement="start"
              label={t(key + "mandatory")}
              control={
                <Checkbox
                  checked={subitem.obrigatoriedade?.preenchimento ?? false}
                  onChange={(eve) => setCheckedMandatory("preenchimento", eve.target.checked)}
                />
              }
            />
            <Typography fontWeight={600}>Conforme:</Typography>
            <FormGroup row>
              <FormControlLabel
                disabled={subitem.hideImg === true}
                label="Foto(s)"
                labelPlacement="start"
                control={
                  <Checkbox
                    checked={subitem.obrigatoriedade?.conforme?.img ?? false}
                    onChange={(eve) => setCheckedMandatoryAvaliation("conforme", "img", eve.target.checked)}
                  />
                }
              />
              <Divider orientation="vertical" flexItem sx={{ marginLeft: 1 }} />
              <FormControlLabel
                disabled={subitem.hideComent === true}
                label="Comentário"
                labelPlacement="start"
                control={
                  <Checkbox
                    checked={subitem.obrigatoriedade?.conforme?.ocorrencia ?? false}
                    onChange={(eve) => setCheckedMandatoryAvaliation("conforme", "ocorrencia", eve.target.checked)}
                  />
                }
              />
              {!modelo?.preenchimentoDeslogado && (
                <>
                  <Divider orientation="vertical" flexItem sx={{ marginLeft: 1 }} />
                  <FormControlLabel
                    disabled={!subitem.permiteArquivo}
                    label="Arquivo(s)"
                    labelPlacement="start"
                    control={
                      <Checkbox
                        checked={subitem.obrigatoriedade?.conforme?.obrigaArquivo ?? false}
                        onChange={(eve) => setCheckedMandatoryAvaliation("conforme", "obrigaArquivo", eve.target.checked)}
                      />
                    }
                  />
                </>
              )}
            </FormGroup>
            <Typography fontWeight={600}>Não conforme:</Typography>
            <FormGroup row>
              <FormControlLabel
                disabled={subitem.hideImg === true}
                label="Foto(s)"
                labelPlacement="start"
                control={
                  <Checkbox
                    checked={subitem.obrigatoriedade?.naoConforme?.img ?? false}
                    onChange={(eve) => setCheckedMandatoryAvaliation("naoConforme", "img", eve.target.checked)}
                  />
                }
              />
              <Divider orientation="vertical" flexItem sx={{ marginLeft: 1 }} />
              <FormControlLabel
                disabled={subitem.hideComent === true}
                label="Comentário"
                labelPlacement="start"
                control={
                  <Checkbox
                    checked={subitem.obrigatoriedade?.naoConforme?.ocorrencia ?? false}
                    onChange={(eve) => setCheckedMandatoryAvaliation("naoConforme", "ocorrencia", eve.target.checked)}
                  />
                }
              />
              {!modelo?.preenchimentoDeslogado && (
                <>
                  <Divider orientation="vertical" flexItem sx={{ marginLeft: 1 }} />
                  <FormControlLabel
                    disabled={!subitem.permiteArquivo}
                    label="Arquivo(s)"
                    labelPlacement="start"
                    control={
                      <Checkbox
                        checked={subitem.obrigatoriedade?.naoConforme?.obrigaArquivo ?? false}
                        onChange={(eve) => setCheckedMandatoryAvaliation("naoConforme", "obrigaArquivo", eve.target.checked)}
                      />
                    }
                  />
                </>
              )}
            </FormGroup>
            {subitem.avaliacao?.some((avalia) => avalia.valor === -1) && (
              <>
                <Typography fontWeight={600}>Não aplica:</Typography>
                <FormGroup row>
                  <FormControlLabel
                    disabled={subitem.hideImg === true}
                    label="Foto(s)"
                    labelPlacement="start"
                    control={
                      <Checkbox
                        checked={(subitem?.obrigatoriedade?.NA && subitem?.obrigatoriedade?.NA?.img) ?? false}
                        onChange={(eve) => setCheckedMandatoryAvaliation("NA", "img", eve.target.checked)}
                      />
                    }
                  />
                  <Divider orientation="vertical" flexItem sx={{ marginLeft: 1 }} />
                  <FormControlLabel
                    disabled={subitem.hideComent === true}
                    label="Comentário(s)"
                    labelPlacement="start"
                    control={
                      <Checkbox
                        checked={subitem.obrigatoriedade?.NA?.ocorrencia ?? false}
                        onChange={(eve) => setCheckedMandatoryAvaliation("NA", "ocorrencia", eve.target.checked)}
                      />
                    }
                  />
                  {!modelo?.preenchimentoDeslogado && (
                    <>
                      <Divider orientation="vertical" flexItem sx={{ marginLeft: 1 }} />
                      <FormControlLabel
                        disabled={!subitem.permiteArquivo}
                        label="Arquivo(s)"
                        labelPlacement="start"
                        control={
                          <Checkbox
                            checked={subitem.obrigatoriedade?.NA?.obrigaArquivo ?? false}
                            onChange={(eve) => setCheckedMandatoryAvaliation("NA", "obrigaArquivo", eve.target.checked)}
                          />
                        }
                      />
                    </>
                  )}
                </FormGroup>
              </>
            )}
          </Stack>
        );
      case "email":
      case "telefone":
      case "rg":
      case "cpfcnpj":
      case "cep":
      case "placa":
      case "textoCurto":
      case "textoLongo":
        return (
          <FormGroup row>
            <FormControlLabel
              labelPlacement="start"
              label={t(key + "mandatory")}
              control={
                <Checkbox
                  checked={subitem.obrigatoriedade?.preenchimento}
                  onChange={(eve) => setCheckedMandatory("preenchimento", eve.target.checked)}
                />
              }
            />
            <Divider orientation="vertical" flexItem sx={{ marginLeft: 1 }} />
            <FormControlLabel
              disabled={!subitem.foto}
              label="Foto(s)"
              labelPlacement="start"
              control={
                <Checkbox
                  checked={(subitem?.obrigatoriedade?.img as boolean) ?? false}
                  onChange={(eve) => setCheckedMandatory("img", eve.target.checked)}
                />
              }
            />
            {!modelo?.preenchimentoDeslogado && (
              <>
                <Divider orientation="vertical" flexItem sx={{ marginLeft: 1 }} />
                <FormControlLabel
                  disabled={!subitem.permiteArquivo}
                  label="Arquivo(s)"
                  labelPlacement="start"
                  control={
                    <Checkbox
                      checked={subitem?.obrigatoriedade?.obrigaArquivo ?? false}
                      onChange={(eve) => setCheckedMandatory("obrigaArquivo", eve.target.checked)}
                    />
                  }
                />
              </>
            )}
          </FormGroup>
        );
      case "data":
      case "hora":
        return (
          <FormGroup row>
            <FormControlLabel
              labelPlacement="start"
              label={t(key + "mandatory")}
              control={
                <Checkbox
                  checked={subitem.obrigatoriedade?.preenchimento ?? false}
                  onChange={(eve) => setCheckedMandatory("preenchimento", eve.target.checked)}
                />
              }
            />
            <Divider orientation="vertical" flexItem sx={{ marginLeft: 1 }} />
            <FormControlLabel
              disabled={!subitem.comentario}
              label="Comentário"
              labelPlacement="start"
              control={
                <Checkbox
                  checked={subitem.obrigatoriedade?.ocorrencia as boolean}
                  onChange={(eve) => setCheckedMandatory("ocorrencia", eve.target.checked)}
                />
              }
            />
          </FormGroup>
        );
      case "multiple":
      case "unique":
      case "numero":
      case "assinatura":
      case "valorMedida":
      case "list":
        return (
          <FormGroup row>
            <FormControlLabel
              labelPlacement="start"
              label={t(key + "mandatory")}
              control={
                <Checkbox
                  checked={subitem.obrigatoriedade?.preenchimento ?? false}
                  onChange={(eve) => setCheckedMandatory("preenchimento", eve.target.checked)}
                />
              }
            />
            <Divider orientation="vertical" flexItem sx={{ marginLeft: 1 }} />
            <FormControlLabel
              disabled={!subitem.foto}
              label="Foto(s)"
              labelPlacement="start"
              control={
                <Checkbox
                  checked={(subitem?.obrigatoriedade?.img as boolean) ?? false}
                  onChange={(eve) => setCheckedMandatory("img", eve.target.checked)}
                />
              }
            />
            <Divider orientation="vertical" flexItem sx={{ marginLeft: 1 }} />
            <FormControlLabel
              disabled={!subitem.comentario}
              label="Comentário"
              labelPlacement="start"
              control={
                <Checkbox
                  checked={(subitem?.obrigatoriedade?.ocorrencia as boolean) ?? false}
                  onChange={(eve) => setCheckedMandatory("ocorrencia", eve.target.checked)}
                />
              }
            />
            {!modelo?.preenchimentoDeslogado && (
              <>
                <Divider orientation="vertical" flexItem sx={{ marginLeft: 1 }} />
                <FormControlLabel
                  disabled={!subitem.permiteArquivo}
                  label="Arquivo(s)"
                  labelPlacement="start"
                  control={
                    <Checkbox
                      checked={subitem.obrigatoriedade?.obrigaArquivo ?? false}
                      onChange={(eve) => setCheckedMandatory("obrigaArquivo", eve.target.checked)}
                    />
                  }
                />
              </>
            )}
          </FormGroup>
        );
    }
  }
  return (
    <Stack>
      <Typography color="textDisabled" fontWeight={600} variant="h6">
        Permissões:
      </Typography>
      {switchPermission(subitem.tipo)}
      <Typography color="textDisabled" fontWeight={600} variant="h6">
        Obrigatoriedades:
      </Typography>
      {switchMandatory(subitem.tipo)}
    </Stack>
  );
}
